/**
 * foamBomTypes.ts
 *
 * Types and helper functions for Product Foam / Packaging BOM.
 */

export type FoamAssemblyType = 'assembled' | 'kd'
export type FoamPackagingType = 'thuong' | 'ecom'

export interface FoamBomConfig {
  id: string
  product_id?: string
  assembly_type: FoamAssemblyType
  packaging_type: FoamPackagingType
  status: 'OK' | 'Đang Cập Nhật' | string
  sort_order: number
  // Thùng 1
  phu_bi_dai?: number | null
  phu_bi_rong?: number | null
  phu_bi_cao?: number | null
  net_weight?: number | null
  gross_weight?: number | null
  // Thùng 2
  phu_bi_2_dai?: number | null
  phu_bi_2_rong?: number | null
  phu_bi_2_cao?: number | null
  net_weight_2?: number | null
  gross_weight_2?: number | null
  // Thùng 3
  phu_bi_3_dai?: number | null
  phu_bi_3_rong?: number | null
  phu_bi_3_cao?: number | null
  net_weight_3?: number | null
  gross_weight_3?: number | null
  created_at?: string
  updated_at?: string
}

export interface FoamBomLine {
  id: string
  /** Standard quote-sheet fields. Legacy rows may omit item_name until converted. */
  item_name?: string
  kt4?: number | string | null
  ncc_supplier_id?: string | null
  loai?: string
  kt1?: number | string | null
  kt2?: number | string | null
  kt3?: number | string | null
  dinh_luong?: number | string | null
  dvt: string
  sl: number
  ghi_chu: string
  type_code?: string | null
  params?: Record<string, any> | null
}

export interface FoamBomSpreadsheetData {
  lines: FoamBomLine[]
}

export interface FoamBomRevision {
  id: string
  revNumber: number
  revLabel: string
  savedAt: string
  savedTime: string
  author: string
  changesSummary: string
  diffs: any[]
  snapshot: {
    lines: FoamBomLine[]
  }
}

export const FOAM_ASSEMBLY_OPTIONS: { value: FoamAssemblyType; label: string }[] = [
  { value: 'kd', label: 'Tháo rời (KD)' },
  { value: 'assembled', label: 'Ráp chết (FA)' },
]

export const FOAM_PACKAGING_OPTIONS: { value: FoamPackagingType; label: string }[] = [
  { value: 'thuong', label: 'Thường' },
  { value: 'ecom', label: 'ECOM' },
]

export function getFoamAssemblyLabel(val: FoamAssemblyType | string): string {
  if (val === 'assembled') return 'Ráp chết (FA)'
  return 'Tháo rời (KD)'
}

export function getFoamPackagingLabel(val: FoamPackagingType | string): string {
  if (val === 'ecom') return 'ECOM'
  return 'Thường'
}

export function createBlankFoamBomLine(): FoamBomLine {
  return {
    id: `fl-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    item_name: '',
    kt1: null,
    kt2: null,
    kt3: null,
    kt4: null,
    dinh_luong: null,
    dvt: 'Pcs',
    sl: 1,
    ghi_chu: '',
  }
}

export function createBlankFoamBomConfig(
  existingConfigs: FoamBomConfig[] = [],
): FoamBomConfig {
  const allCombos: { assembly_type: FoamAssemblyType; packaging_type: FoamPackagingType }[] = [
    { assembly_type: 'kd', packaging_type: 'thuong' },
    { assembly_type: 'kd', packaging_type: 'ecom' },
    { assembly_type: 'assembled', packaging_type: 'thuong' },
    { assembly_type: 'assembled', packaging_type: 'ecom' },
  ]

  // Pick first unused combination if available
  const unused = allCombos.find(
    (combo) =>
      !existingConfigs.some(
        (c) => c.assembly_type === combo.assembly_type && c.packaging_type === combo.packaging_type,
      ),
  )

  const selected = unused || allCombos[0]

  return {
    id: `fcfg-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
    assembly_type: selected.assembly_type,
    packaging_type: selected.packaging_type,
    status: 'Đang Cập Nhật',
    sort_order: existingConfigs.length,
    phu_bi_dai: null,
    phu_bi_rong: null,
    phu_bi_cao: null,
    net_weight: null,
    gross_weight: null,
  }
}

export function compareFoamBomLines(
  baseline: FoamBomLine[],
  current: FoamBomLine[],
): { type: string; id?: string; field?: string; oldVal?: any; newVal?: any }[] {
  const diffs: { type: string; id?: string; field?: string; oldVal?: any; newVal?: any }[] = []
  const baseMap = new Map(baseline.map((l) => [l.id, l]))
  const currMap = new Map(current.map((l) => [l.id, l]))

  // Check added or changed
  for (const curr of current) {
    const base = baseMap.get(curr.id)
    if (!base) {
      diffs.push({ type: 'row_added', id: curr.id, newVal: curr })
    } else {
      const fields: (keyof FoamBomLine)[] = [
        'item_name',
        'ncc_supplier_id',
        'loai',
        'kt1',
        'kt2',
        'kt3',
        'kt4',
        'dinh_luong',
        'dvt',
        'sl',
        'ghi_chu',
      ]
      for (const f of fields) {
        if (curr[f] !== base[f]) {
          diffs.push({
            type: 'cell_change',
            id: curr.id,
            field: f,
            oldVal: base[f],
            newVal: curr[f],
          })
        }
      }
    }
  }

  // Check deleted
  for (const base of baseline) {
    if (!currMap.has(base.id)) {
      diffs.push({ type: 'row_deleted', id: base.id, oldVal: base })
    }
  }

  return diffs
}

export function isFoamBomSpreadsheetDirty(
  current: FoamBomLine[],
  baseline: FoamBomLine[] | null,
  isEditMode: boolean,
): boolean {
  if (!isEditMode || !baseline) return false
  const diffs = compareFoamBomLines(baseline, current)
  return diffs.length > 0
}
